import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useBack } from '../lib/back.js';
import type { Activity, ActivityDetail, ActivityKind, TrainingNight } from '@keetcheck/shared';
import { theme } from '../theme.js';
import { useAuth, useAccess } from '../context/AuthContext.js';
import {
  useActivities, useActivity, useRespondActivity, useSaveActivity, useCancelActivity,
  useTrainingNight, useSaveTrainingNight, useTeams, useUnitCatalogue, useDutyRoster,
} from '../lib/queries.js';
import { PageShell } from '../components/PageShell.js';
import { Icon } from '../components/Icon.js';
import { TeamChip } from '../components/RosterGrid.js';
import { Pill } from '../components/CoverBits.js';
import { emitError } from '../lib/toastBus.js';
import { rangeLabel, dayLabel, localDay, hhmm, shortDate, timeRange } from '../lib/shifts.js';

export const ACTIVITY_LABEL: Record<ActivityKind, string> = {
  training: 'Training night', power_maintenance: 'Power maintenance', skills: 'Skills session',
  community: 'Community engagement', other: 'Activity',
};
const WEEKDAYS = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// ── List (Shifts → Activities tab) ─────────────────────────────

export function ActivitiesList() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { isAdmin } = useAccess();
  const orgId = user?.orgId ?? '';
  const { data: list = [], isLoading } = useActivities(orgId);
  const days = [...new Set(list.map((a) => localDay(a.startsAt)))];

  return (
    <>
      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, padding: '10px 16px', flexWrap: 'wrap' }}>
          <button onClick={() => nav('/activity/new')} style={primarySm}><Icon name="plus" size={14} /> New activity</button>
          <TrainingNightButton orgId={orgId} />
        </div>
      )}
      {isLoading && <Empty>Loading…</Empty>}
      {!isLoading && !list.length && <Empty>Nothing planned in the next 8 weeks.</Empty>}
      {days.map((d) => (
        <section key={d}>
          <Header>{dayLabel(list.find((a) => localDay(a.startsAt) === d)!.startsAt)}</Header>
          {list.filter((a) => localDay(a.startsAt) === d).map((a) => <ActivityRow key={a.id} a={a} />)}
        </section>
      ))}
    </>
  );
}

function ActivityRow({ a }: { a: Activity }) {
  const nav = useNavigate();
  return (
    <button onClick={() => nav(`/activity/${a.id}`)} style={{ ...row, opacity: a.cancelled ? 0.55 : 1 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: theme.text, textDecoration: a.cancelled ? 'line-through' : 'none' }}>{a.title}</span>
          {a.hostTeam && <TeamChip name={a.hostTeam.name} />}
          {a.cancelled && <Pill tone="muted">Cancelled</Pill>}
          {!a.cancelled && a.myResponse && <ResponsePill r={a.myResponse} signup={a.responseMode === 'signup'} />}
        </div>
        <div style={{ fontSize: 12, color: theme.textMuted }}>
          {a.title !== ACTIVITY_LABEL[a.kind] && `${ACTIVITY_LABEL[a.kind]} · `}{new Date(a.endsAt).getTime() - new Date(a.startsAt).getTime() <= 24 * 3600_000
            ? timeRange(a.startsAt, a.endsAt) : rangeLabel(a.startsAt, a.endsAt)}
          {a.location ? ` · ${a.location}` : ''}
        </div>
        {a.responseMode === 'signup' && a.peopleNeeded && !a.cancelled && (
          <div style={{ fontSize: 12, fontWeight: 700, color: a.full ? theme.status.ok.fg : theme.status.warn.fg }}>
            {a.going} of {a.peopleNeeded} signed up{a.full ? ' — full' : ''}
          </div>
        )}
        {a.responseMode === 'rsvp' && !a.cancelled && (a.going > 0 || a.maybe > 0) && (
          <div style={{ fontSize: 12, color: theme.textMuted }}>{a.going} going{a.maybe ? ` · ${a.maybe} maybe` : ''}</div>
        )}
      </div>
      <Icon name="chevron" size={16} style={{ color: theme.textMuted }} />
    </button>
  );
}

function ResponsePill({ r, signup }: { r: 'going' | 'maybe' | 'no'; signup: boolean }) {
  if (r === 'going') return <Pill tone="ok">{signup ? 'Signed up' : 'Going'}</Pill>;
  if (r === 'maybe') return <Pill tone="warn">Maybe</Pill>;
  return <Pill tone="muted">Can’t</Pill>;
}

function TrainingNightButton({ orgId }: { orgId: string }) {
  const { data: tn } = useTrainingNight(orgId);
  const [open, setOpen] = useState(false);
  if (!tn) return null;
  return (
    <>
      <button onClick={() => setOpen(!open)} style={secondarySm}>
        Training night: {tn.enabled ? `${WEEKDAYS[tn.weekday]!.slice(0, 3)} ${tn.startTime}` : 'off'}
      </button>
      {open && <TrainingNightForm orgId={orgId} tn={tn} onDone={() => setOpen(false)} />}
    </>
  );
}

function TrainingNightForm({ orgId, tn, onDone }: { orgId: string; tn: TrainingNight; onDone: () => void }) {
  const save = useSaveTrainingNight(orgId);
  const [d, setD] = useState(tn);
  return (
    <div style={{ ...card, flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label style={check}><input type="checkbox" checked={d.enabled} onChange={(e) => setD({ ...d, enabled: e.target.checked })} /> Weekly training night</label>
      <input value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} aria-label="Title" style={input} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={d.weekday} onChange={(e) => setD({ ...d, weekday: Number(e.target.value) })} aria-label="Day" style={input}>
          {WEEKDAYS.slice(1).map((w, i) => <option key={w} value={i + 1}>{w}</option>)}
        </select>
        <input type="time" value={d.startTime} onChange={(e) => setD({ ...d, startTime: e.target.value })} aria-label="Starts" style={input} />
        <span style={{ color: theme.textMuted }}>–</span>
        <input type="time" value={d.endTime} onChange={(e) => setD({ ...d, endTime: e.target.value })} aria-label="Ends" style={input} />
      </div>
      <input value={d.location ?? ''} onChange={(e) => setD({ ...d, location: e.target.value || null })} placeholder="Where (optional)" aria-label="Where" style={input} />
      <div style={{ fontSize: 12, color: theme.textMuted }}>Shows for everyone operational, with their team's checks for that week. No replies needed.</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button disabled={save.isPending} onClick={() => save.mutate(d, { onSuccess: onDone, onError: (e) => emitError(e instanceof Error ? e.message : 'Could not save') })} style={primarySm}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onDone} style={secondarySm}>Cancel</button>
      </div>
    </div>
  );
}

// ── Detail ─────────────────────────────────────────────────────

export function ActivityPage() {
  const { id = '' } = useParams();
  const back = useBack('/shifts?tab=activities');
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const { data: a, isLoading, error } = useActivity(orgId, id);
  return (
    <PageShell noNav>
      <TopBar title={a ? ACTIVITY_LABEL[a.kind] : 'Activity'} onBack={back} />
      <div style={body}>
        {isLoading && <div style={muted}>Loading…</div>}
        {error && <div style={muted}>{(error as Error).message}</div>}
        {a && <Detail orgId={orgId} a={a} />}
      </div>
    </PageShell>
  );
}

function Detail({ orgId, a }: { orgId: string; a: ActivityDetail }) {
  const nav = useNavigate();
  const respond = useRespondActivity(orgId, a.id);
  const cancel = useCancelActivity(orgId, a.id);
  const started = new Date(a.startsAt) <= new Date();
  const canReply = !a.cancelled && !started && a.responseMode !== 'none';
  const reply = (r: 'going' | 'maybe' | 'no' | null) =>
    respond.mutate(r, { onError: (e) => emitError(e instanceof Error ? e.message : 'Could not save') });

  return (
    <>
      <div style={card}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
          {a.hostTeam && <span style={{ fontSize: 12, color: theme.textDim }}><TeamChip name={a.hostTeam.name} /> hosting</span>}
          {a.cancelled && <Pill tone="danger">Cancelled</Pill>}
        </div>
        <div style={{ fontSize: 18, fontWeight: 800, color: theme.text }}>{a.title}</div>
        <div style={{ fontSize: 14, color: theme.textDim }}>{rangeLabel(a.startsAt, a.endsAt)}</div>
        {a.location && <div style={{ fontSize: 13, color: theme.textDim, marginTop: 2 }}><Icon name="pin" size={12} /> {a.location}</div>}
        {a.description && <div style={{ fontSize: 13, color: theme.text, marginTop: 8, whiteSpace: 'pre-wrap' }}>{a.description}</div>}
      </div>

      {a.myTeamChecks && (
        <div style={card}>
          <div style={label}>Your team's checks this week</div>
          {a.myTeamChecks.length === 0 && <div style={muted}>Nothing assigned to your team this week.</div>}
          {a.myTeamChecks.map((c) => (
            <button key={c.runId} onClick={() => nav(`/checks/${c.runId}`)} style={{ ...rowFlat }}>
              <span style={{ flex: 1, fontWeight: 600, color: theme.text }}>{c.assetName}</span>
              <Pill tone={c.status === 'submitted' ? 'ok' : 'warn'}>{c.status === 'submitted' ? 'Done' : 'To do'}</Pill>
              <Icon name="chevron" size={14} />
            </button>
          ))}
        </div>
      )}

      {a.responseMode === 'signup' && a.peopleNeeded && (
        <div style={{ ...card, background: a.full ? theme.status.ok.bg : theme.status.warn.bg }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: a.full ? theme.status.ok.fg : theme.status.warn.fg }}>
            {a.going} of {a.peopleNeeded} signed up{a.full ? ' — full, thanks!' : ''}
          </div>
          {a.wantedQuals.length > 0 && (
            <div style={{ fontSize: 12, color: theme.textDim, marginTop: 4 }}>
              Good to have: {a.wantedQuals.map((c) => `${c} (${a.qualCoverage[c] ?? 0} so far)`).join(' · ')}
            </div>
          )}
        </div>
      )}

      {canReply && (
        <div style={card}>
          <div style={label}>{a.responseMode === 'signup' ? 'Can you help?' : 'Are you coming?'}</div>
          {a.responseMode === 'signup' ? (
            a.myResponse === 'going'
              ? <button disabled={respond.isPending} onClick={() => reply(null)} style={{ ...secondary, width: '100%' }}>Withdraw — I can't make it</button>
              : <button disabled={respond.isPending || a.full} onClick={() => reply('going')} style={{ ...primary, width: '100%', opacity: a.full ? 0.5 : 1 }}>{a.full ? 'Full' : 'Sign me up'}</button>
          ) : (
            <div style={{ display: 'flex', gap: 6 }}>
              {(['going', 'maybe', 'no'] as const).map((r) => (
                <button key={r} disabled={respond.isPending} onClick={() => reply(a.myResponse === r ? null : r)} style={choice(a.myResponse === r)}>
                  {r === 'going' ? 'Going' : r === 'maybe' ? 'Maybe' : 'Can’t'}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {a.responseMode !== 'none' && (
        <div style={card}>
          <div style={label}>{a.responseMode === 'signup' ? 'Signed up' : 'Going'} ({a.responses.going.length})</div>
          <div style={{ fontSize: 13, color: theme.text }}>{a.responses.going.map((p) => p.displayName).join(', ') || '—'}</div>
          {a.responses.maybe.length > 0 && <><div style={{ ...label, marginTop: 8 }}>Maybe ({a.responses.maybe.length})</div>
            <div style={{ fontSize: 13, color: theme.textDim }}>{a.responses.maybe.map((p) => p.displayName).join(', ')}</div></>}
          {a.responses.no.length > 0 && <><div style={{ ...label, marginTop: 8 }}>Can't ({a.responses.no.length})</div>
            <div style={{ fontSize: 13, color: theme.textMuted }}>{a.responses.no.map((p) => p.displayName).join(', ')}</div></>}
        </div>
      )}

      {a.canEdit && !a.cancelled && !started && (
        <div style={{ display: 'flex', gap: 8 }}>
          {a.kind !== 'training' && <button onClick={() => nav(`/activity/${a.id}/edit`)} style={{ ...secondary, flex: 1 }}>Edit</button>}
          <button disabled={cancel.isPending} onClick={() => { if (confirm('Cancel this activity? People who replied will be told.')) cancel.mutate(); }}
            style={{ ...secondary, flex: 1, color: theme.status.danger.fg }}>Cancel activity</button>
        </div>
      )}
    </>
  );
}

// ── Create / edit (admins) ─────────────────────────────────────

const DEFAULT_TITLE: Record<Exclude<ActivityKind, 'training'>, string> = {
  power_maintenance: 'Power maintenance', skills: 'Boat skills', community: '', other: '',
};

export function ActivityFormPage() {
  const { id } = useParams();
  const back = useBack(id ? `/activity/${id}` : '/shifts?tab=activities');
  const [params] = useSearchParams();
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const { data: existing } = useActivity(orgId, id ?? '');
  if (id && !existing) return <PageShell noNav><TopBar title="Edit activity" onBack={back} /><div style={body}><div style={muted}>Loading…</div></div></PageShell>;
  return <ActivityForm orgId={orgId} existing={existing ?? null} initialKind={(params.get('kind') as ActivityKind) ?? 'community'} />;
}

function ActivityForm({ orgId, existing, initialKind }: { orgId: string; existing: ActivityDetail | null; initialKind: ActivityKind }) {
  const nav = useNavigate();
  const back = useBack(existing ? `/activity/${existing.id}` : '/shifts?tab=activities');
  const save = useSaveActivity(orgId, existing?.id);
  const { data: teams = [] } = useTeams(orgId);
  const { data: catalogue } = useUnitCatalogue(orgId);
  const [kind, setKind] = useState<Exclude<ActivityKind, 'training'>>(existing?.kind === 'training' ? 'other' : (existing?.kind as never) ?? (initialKind === 'training' ? 'community' : initialKind as never));
  const [title, setTitle] = useState(existing?.title ?? DEFAULT_TITLE[kind]);
  const [date, setDate] = useState(existing ? localDay(existing.startsAt) : '');
  const [start, setStart] = useState(existing ? hhmm(existing.startsAt) : '09:00');
  const [end, setEnd] = useState(existing ? hhmm(existing.endsAt) : '13:00');
  const [location, setLocation] = useState(existing?.location ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [host, setHost] = useState<string>(existing?.hostTeam?.id ?? 'auto');
  const [needed, setNeeded] = useState<string>(existing?.peopleNeeded ? String(existing.peopleNeeded) : '');
  const [quals, setQuals] = useState<Set<string>>(new Set(existing?.wantedQuals ?? []));
  const [notify, setNotify] = useState(true);
  // who'd host by default: the duty crew of that week (power maintenance)
  const monday = date ? (() => { const d = new Date(`${date}T12:00:00`); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return localDay(d.toISOString()); })() : undefined;
  const { data: roster } = useDutyRoster(orgId, 1, monday);
  const dutyTeam = teams.find((t) => t.id === roster?.weeks[0]?.dutyTeamId);

  function submit() {
    if (!title.trim() || !date) { emitError('Add a title and a date'); return; }
    const startsAt = new Date(`${date}T${start}:00`);
    const endsAt = new Date(`${date}T${end}:00`);
    if (endsAt <= startsAt) endsAt.setDate(endsAt.getDate() + 1); // runs past midnight
    const body = {
      title: title.trim(), startsAt: startsAt.toISOString(), endsAt: endsAt.toISOString(),
      location: location.trim() || null, description: description.trim() || null,
      ...(host === 'auto' ? {} : { hostTeamId: host || null }),
      ...(kind === 'community' ? { peopleNeeded: needed ? Number(needed) : null, wantedQuals: [...quals] } : {}),
      ...(existing ? {} : { kind, notify }),
    };
    save.mutate(body, {
      onSuccess: (a) => (existing ? back() : nav(`/activity/${a.id}`, { replace: true })),
      onError: (e) => emitError(e instanceof Error ? e.message : 'Could not save'),
    });
  }

  return (
    <PageShell noNav>
      <TopBar title={existing ? 'Edit activity' : 'New activity'} onBack={back} />
      <div style={body}>
        <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {!existing && (
            <label style={field}>Kind
              <select value={kind} onChange={(e) => { const k = e.target.value as typeof kind; setKind(k); if (!title || Object.values(DEFAULT_TITLE).includes(title)) setTitle(DEFAULT_TITLE[k]); }} style={input}>
                <option value="power_maintenance">Power maintenance</option>
                <option value="skills">Skills session</option>
                <option value="community">Community engagement</option>
                <option value="other">Other</option>
              </select>
            </label>
          )}
          <label style={field}>Title<input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} style={input} /></label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <label style={{ ...field, flex: '1 1 150px' }}>Date<input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={input} /></label>
            <label style={{ ...field, flex: '0 1 110px' }}>From<input type="time" value={start} onChange={(e) => setStart(e.target.value)} style={input} /></label>
            <label style={{ ...field, flex: '0 1 110px' }}>To<input type="time" value={end} onChange={(e) => setEnd(e.target.value)} style={input} /></label>
          </div>
          <label style={field}>Where<input value={location} onChange={(e) => setLocation(e.target.value)} maxLength={200} placeholder="Optional" style={input} /></label>
          <label style={field}>Host team
            <select value={host} onChange={(e) => setHost(e.target.value)} style={input}>
              {!existing && <option value="auto">{kind === 'power_maintenance' ? `That week's duty crew${dutyTeam ? ` (${dutyTeam.name})` : ''}` : 'None'}</option>}
              {existing && <option value="">None</option>}
              {teams.filter((t) => t.active).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          {kind === 'community' && (
            <>
              <label style={field}>People needed<input type="number" min={1} max={200} value={needed} onChange={(e) => setNeeded(e.target.value)} style={{ ...input, width: 100 }} /></label>
              <div style={field}>Good to have (optional)
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {(catalogue?.qualifications ?? []).filter((q) => q.active).map((q) => (
                    <button key={q.code} type="button" onClick={() => { const n = new Set(quals); if (n.has(q.code)) n.delete(q.code); else n.add(q.code); setQuals(n); }}
                      style={choiceSm(quals.has(q.code))}>{q.code}</button>
                  ))}
                </div>
              </div>
            </>
          )}
          <label style={field}>Details<textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={2000} style={{ ...input, resize: 'vertical' }} /></label>
          {!existing && <label style={check}><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} /> Tell everyone now (notification, or email if they haven’t turned notifications on)</label>}
        </div>
        <button disabled={save.isPending} onClick={submit} style={primary}>{save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create activity'}</button>
        {date && kind === 'power_maintenance' && !existing && (
          <div style={muted}>Week of {monday ? shortDate(monday) : ''}{dutyTeam ? ` — ${dutyTeam.name} is the duty crew and hosts by default.` : ''}</div>
        )}
      </div>
    </PageShell>
  );
}

// ── bits ───────────────────────────────────────────────────────

function TopBar({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div style={{
      padding: '12px 16px', background: theme.surface, borderBottom: `1px solid ${theme.border}`,
      display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 5,
    }}>
      <button onClick={onBack} aria-label="Back" style={iconBtn}><Icon name="back" size={18} /></button>
      <div style={{ fontSize: 17, fontWeight: 700, color: theme.text }}>{title}</div>
    </div>
  );
}

function Header({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: '8px 16px', fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: theme.textMuted, background: theme.bg, borderBottom: `1px solid ${theme.border}` }}>{children}</div>;
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: '32px 24px', textAlign: 'center', fontSize: 13, color: theme.textMuted }}>{children}</div>;
}

const body: React.CSSProperties = { flex: 1, overflowY: 'auto', background: theme.bg, padding: '14px 16px 32px', display: 'flex', flexDirection: 'column', gap: 14 };
const card: React.CSSProperties = { padding: '12px 14px', borderRadius: 12, background: theme.surface, border: `1px solid ${theme.border}` };
const label: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: theme.textMuted, marginBottom: 6, display: 'block' };
const muted: React.CSSProperties = { fontSize: 13, color: theme.textMuted, lineHeight: 1.5 };
const field: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: theme.textDim };
const check: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: theme.text };
const input: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface,
  color: theme.text, fontFamily: theme.font, fontSize: 14, boxSizing: 'border-box', minWidth: 0,
};
const row: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', width: '100%', textAlign: 'left',
  background: theme.surface, border: 'none', borderBottom: `1px solid ${theme.border}`, cursor: 'pointer', fontFamily: theme.font,
};
const rowFlat: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', width: '100%', textAlign: 'left',
  background: 'none', border: 'none', borderTop: `1px solid ${theme.border}`, cursor: 'pointer', fontFamily: theme.font, fontSize: 14,
};
const primary: React.CSSProperties = {
  padding: '12px 0', borderRadius: 12, border: 'none', fontSize: 15, fontWeight: 700,
  fontFamily: theme.font, background: theme.accent, color: theme.accentInk, cursor: 'pointer',
};
const secondary: React.CSSProperties = {
  padding: '11px 14px', borderRadius: 10, border: `1px solid ${theme.border}`, fontSize: 14, fontWeight: 700,
  fontFamily: theme.font, background: theme.surface, color: theme.text, cursor: 'pointer',
};
const primarySm: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 12px', borderRadius: 8, border: 'none', fontSize: 13,
  fontWeight: 700, fontFamily: theme.font, background: theme.accent, color: theme.accentInk, cursor: 'pointer',
};
const secondarySm: React.CSSProperties = {
  padding: '7px 12px', borderRadius: 8, border: `1px solid ${theme.border}`, fontSize: 13, fontWeight: 700,
  fontFamily: theme.font, background: theme.surface, color: theme.text, cursor: 'pointer',
};
const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`, background: theme.surface2, color: theme.text,
  borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};
const choice = (on: boolean): React.CSSProperties => ({
  flex: 1, padding: '10px 0', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font,
  border: `1px solid ${on ? theme.accent : theme.border}`, background: on ? theme.accent : theme.surface, color: on ? theme.accentInk : theme.text,
});
const choiceSm = (on: boolean): React.CSSProperties => ({
  padding: '4px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font,
  border: `1px solid ${on ? theme.accent : theme.border}`, background: on ? theme.accent : theme.surface, color: on ? theme.accentInk : theme.textDim,
});
